/**
 * OrgScopePill — the active-org scope control for meta-admins.
 *
 * The pill NAMES the organization it is scoped to and lists the organizations
 * available to switch to. It used to render the raw cuid and offer a menu
 * containing only that same cuid, so the control disagreed with the
 * Organizations screen it was supposed to mirror: the list showed "Sextant
 * Properties", the pill showed `org: cmra27rx10000n00kiznv7ktd`, and switching
 * meant leaving for another screen entirely.
 *
 * The id is kept as a subtitle in the menu — it is what appears in API calls and
 * support requests, so it should be readable, just not the primary label.
 */
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon } from './ui/icons.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from './ui/index.js';

/** Stable accent tint for an org's logo chip, derived from its id. */
const LOGO_TINTS = ['#b07a4a', '#3b6ef0', '#16a974', '#5b5bd6', '#c9810b', '#d9488a'];
function tintFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return LOGO_TINTS[h % LOGO_TINTS.length] ?? LOGO_TINTS[0]!;
}

export interface OrgScopePillProps {
  /** The org id the meta-admin is currently acting within. */
  orgId: string;
  /** Every organization this admin may switch to; may be empty while loading. */
  organizations?: { id: string; name: string }[];
  /** Switch the active scope to another organization. */
  onSelect?: (id: string) => void;
  /** Clear the active scope (meta-admin only). */
  onClearScope: () => void;
}

export function OrgScopePill({
  orgId,
  organizations = [],
  onSelect,
  onClearScope,
}: OrgScopePillProps): ReactElement {
  const navigate = useNavigate();

  const active = organizations.find((o) => o.id === orgId);
  // Until the list arrives the id is all we have — better than an empty pill,
  // and it is replaced the moment the fetch resolves.
  const activeLabel = active?.name ?? orgId;

  const leaveScope = (): void => {
    onClearScope();
    navigate('/meta/orgs');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Acting within this org — switch organizations"
          className={cn(
            'inline-flex h-8 flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-md',
            'border border-border bg-surface-2 pl-2.5 pr-[9px]',
            'text-[12.5px] font-semibold text-text transition-colors hover:border-border-strong',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          <span
            aria-hidden="true"
            className="grid h-[17px] w-[17px] place-items-center rounded-[5px] text-[9px] font-extrabold text-white"
            style={{ background: tintFor(orgId) }}
          >
            {activeLabel.replace(/^org[_-]?/i, '').charAt(0).toUpperCase() || 'O'}
          </span>
          org: {activeLabel}
          <Icon.chevDown size={13} className="text-faint" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[260px]">
        <DropdownMenuLabel>Organization scope</DropdownMenuLabel>
        {(organizations.length > 0 ? organizations : [{ id: orgId, name: activeLabel }]).map(
          (org) => (
            <DropdownMenuItem
              key={org.id}
              onSelect={() => {
                if (org.id !== orgId) onSelect?.(org.id);
              }}
            >
              <span
                aria-hidden="true"
                className="grid h-[17px] w-[17px] place-items-center rounded-[5px] text-[9px] font-extrabold text-white"
                style={{ background: tintFor(org.id) }}
              >
                {org.name.charAt(0).toUpperCase() || 'O'}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{org.name}</span>
                <span className="truncate text-[10.5px] text-faint">{org.id}</span>
              </span>
              {org.id === orgId ? (
                <Icon.check size={15} className="ml-auto !text-primary" />
              ) : null}
            </DropdownMenuItem>
          ),
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={leaveScope}>
          <Icon.org size={15} />
          All organizations
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
